import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  SABLON_BASLIK_EN_COK,
  SABLON_EN_COK_ADET,
  SABLON_GOVDE_EN_COK,
} from "@koclukpanel/contracts";
import { Alan, Buton, Iskelet, Kart, MetinAlani } from "@koclukpanel/ui";
import { ApiHatasi, mesajSablonlarim, mesajSablonuArsivle, mesajSablonuEkle } from "./api";

/**
 * HAZIR YANITLAR — F-MSG-04
 *
 * ================================================================
 * ŞABLON KİŞİSEL
 * ================================================================
 * Şartname: *"Hazır mesaj şablonları (uzman için sık kullanılan
 * yanıtlar)."* Uçlar `@KimlikGerekli()` — kişi yalnızca kendi
 * şablonlarını görüyor. Ayrı bir izin yok; olsaydı o izni taşımayan
 * koç sık kullandığı yanıtı kaydedemez ve madde çalışmazdı.
 *
 * ================================================================
 * ŞABLON YAPIŞTIRILIR, GÖNDERİLMEZ
 * ================================================================
 * Seçilen metin yazma kutusuna KONUYOR; doğrudan gönderilmiyor.
 * Doğrudan gönderseydik hazır yanıt, okunmadan gönderilen bir yanıt
 * olurdu — ve bu modülün karşısındaki kişi bir çocuk.
 */
export function MesajSablonlari() {
  const qc = useQueryClient();
  const [acik, setAcik] = useState(false);
  const [baslik, setBaslik] = useState("");
  const [govde, setGovde] = useState("");
  const [hata, setHata] = useState<string | null>(null);

  const veri = useQuery({ queryKey: ["mesaj-sablonlar"], queryFn: mesajSablonlarim, retry: false });

  const tazele = () => qc.invalidateQueries({ queryKey: ["mesaj-sablonlar"] });

  const ekle = useMutation({
    mutationFn: () => mesajSablonuEkle({ baslik, govde }),
    onSuccess: async () => {
      setBaslik("");
      setGovde("");
      setAcik(false);
      setHata(null);
      await tazele();
    },
    onError: (e) => setHata(e instanceof ApiHatasi ? e.message : "Kaydedilemedi."),
  });

  const kaldir = useMutation({
    mutationFn: (id: string) => mesajSablonuArsivle(id),
    onSuccess: async () => {
      setHata(null);
      await tazele();
    },
    onError: (e) => setHata(e instanceof ApiHatasi ? e.message : "Kaldırılamadı."),
  });

  if (veri.isPending) return <Iskelet satir={2} />;
  /*
    MÜŞTERİYE ŞABLON YOK — bölüm hiç çizilmiyor. Boş bir "hazır
    yanıtlarım" kutusu, öğrenciye ait olmayan bir aracı ona aitmiş gibi
    gösterirdi.
  */
  if (veri.isError || !veri.data.personelMi) return null;

  const sablonlar = veri.data.sablonlar;

  return (
    <Kart
      baslik="Hazır yanıtlarım"
      aciklama="Sık yazdığınız yanıtları kaydedin; yazışmada tek tıkla mesaj kutusuna gelir."
      eylem={
        <Buton gorunum={acik ? "ikincil" : "birincil"} onClick={() => setAcik((o) => !o)}>
          {acik ? "Vazgeç" : "Yeni yanıt"}
        </Buton>
      }
    >
      <div className="flex flex-col gap-3">
        {acik && (
          <div className="bg-notr-50 rounded-alan flex flex-col gap-2 p-3">
            <Alan
              etiket="Başlık"
              yardim="Listede bunu göreceksiniz — örneğin “Program yayınlandı”."
              maxLength={SABLON_BASLIK_EN_COK}
              value={baslik}
              onChange={(e) => setBaslik(e.target.value)}
            />
            <MetinAlani
              etiket="Yanıt metni"
              rows={4}
              maxLength={SABLON_GOVDE_EN_COK}
              value={govde}
              onChange={(e) => setGovde(e.target.value)}
            />
            <div>
              <Buton
                onClick={() => ekle.mutate()}
                yukleniyor={ekle.isPending}
                yukleniyorMetni="Kaydediliyor"
                disabled={baslik.trim().length === 0 || govde.trim().length === 0}
              >
                Kaydet
              </Buton>
            </div>
          </div>
        )}

        {sablonlar.length === 0 ? (
          <p className="text-notr-700 text-sm">
            Henüz hazır yanıtınız yok. En fazla {SABLON_EN_COK_ADET} tane tutabilirsiniz.
          </p>
        ) : (
          <ul className="flex flex-col">
            {sablonlar.map((s) => (
              <li
                key={s.id}
                className="border-notr-200 flex flex-wrap items-start justify-between gap-2 border-b py-2 last:border-b-0"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-notr-900 text-sm font-medium">{s.baslik}</p>
                  {/*
                    Gövde KIRPILARAK gösteriliyor: liste bir önizleme,
                    okuma yeri değil. Tamamı yazışmada kutuya gelince
                    görünüyor ve orada düzenlenebiliyor.
                  */}
                  <p className="text-notr-600 mt-0.5 line-clamp-2 text-xs">{s.govde}</p>
                </div>
                <Buton
                  gorunum="sessiz"
                  onClick={() => kaldir.mutate(s.id)}
                  yukleniyor={kaldir.isPending && kaldir.variables === s.id}
                  yukleniyorMetni="Kaldırılıyor"
                >
                  Kaldır
                </Buton>
              </li>
            ))}
          </ul>
        )}

        {hata && (
          <p role="alert" className="text-hata-700 text-sm">
            {hata}
          </p>
        )}
      </div>
    </Kart>
  );
}
